import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';

import { cn } from '../../lib/utils';
import { Input } from './input';

type SecretInputProps = Omit<
  React.ComponentPropsWithoutRef<'input'>,
  'type'
> & {
  /** Classes for the wrapper, e.g. its width in a flex row. */
  containerClassName?: string;
};

/**
 * A field for an API key or token. It's a text field hidden with CSS, not a
 * password field: browsers offer to save anything typed into a password
 * field as a login, whatever its autocomplete says.
 */
const SecretInput = React.forwardRef<HTMLInputElement, SecretInputProps>(
  ({ className, containerClassName, style, ...props }, ref) => {
    const [visible, setVisible] = React.useState(false);
    return (
      <div className={cn('relative w-full', containerClassName)}>
        <Input
          ref={ref}
          type="text"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          data-1p-ignore="true"
          data-lpignore="true"
          data-bwignore="true"
          data-form-type="other"
          style={
            visible
              ? style
              : ({
                  ...style,
                  WebkitTextSecurity: 'disc',
                } as React.CSSProperties)
          }
          className={cn('pr-9', className)}
          {...props}
        />
        <button
          type="button"
          onClick={() => setVisible((value) => !value)}
          aria-label={visible ? 'Hide' : 'Show'}
          aria-pressed={visible}
          className="absolute inset-y-0 right-0 flex w-9 items-center justify-center text-prompt-safecomabbey hover:text-prompt-safecomvulcan focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          {visible ? (
            <EyeOff className="h-4 w-4" />
          ) : (
            <Eye className="h-4 w-4" />
          )}
        </button>
      </div>
    );
  },
);
SecretInput.displayName = 'SecretInput';

export { SecretInput };
